import React, { useState, useEffect } from "react";

import {
  CircularProgress,
  CssBaseline,
  Grid,
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useMutation, useQuery } from "@tanstack/react-query";
import browser from "browser-detect";
import { useParams } from "react-router-dom";
import Video from "twilio-video";

import packageJson from "../../../../package.json";
import { useAppState } from "../../../context/StateContext";
import TaskService from "../../../services/task.service";
import VideoSurveyService from "../../../services/video.survey.service";
import { EnterVideoSurvey } from "../../../types";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import useRoomState from "../hooks/useRoomState/useRoomState";
import useVideoContext from "../hooks/useVideoContext/useVideoContext";
import Leave from "../Leave";
import PreJoinModal from "../PreJoinModal";
import SurveyCall from "../SurveyCall";
import theme from "./theme";

const useStyles = makeStyles(() => ({
  mainDiv: {
    overflow: "hidden",
    height: "100vh",
    width: "100%",
  },
}));

const SurveyScreen = () => {
  const { taskId } = useParams<{ taskId?: string | undefined }>();

  const roomState = useRoomState();

  const classes = useStyles();

  const {
    connect: videoConnect,
    isAcquiringLocalTracks,
    isConnecting,
    setTask,
    videoSurvey,
    setVideoSurvey,
    isFinished,
    room,
  } = useVideoContext();

  const { mobile = null, name = null, os = null, version = null } = browser();

  const { currentUser } = useAppState();

  const [hasError, setHasError] = useState(false);
  const [isAcquiringPermissions, setIsAcquiringPermissions] = useState(true);

  const { isFetching: loadingTask } = useQuery(
    ["videocall.tasks.get"],
    () =>
      TaskService.getById(Number(taskId), {
        include: JSON.stringify([
          {
            association: "job",
            include: [
              { association: "customer" },
              {
                association: "origins",
                order: [["createdAt", "desc"]],
              },
              { association: "destinations", order: [["createdAt", "desc"]] },
            ],
          },
        ]),
      }),
    {
      enabled: !!taskId,
      onSuccess: (response) => {
        const task = response?.data;

        if (task) {
          setTask(task);
          enterVideoSurvey({
            roomNameOrEmailCustomer: task?.roomName,
            identity: currentUser?.name,
            isSupportedTwilio: Video.isSupported,
            isMobile: mobile,
            browserName: name,
            os,
            platform: "Web",
            appVersion: packageJson.version,
            browserVersion: version,
          });
        }
      },
    }
  );

  const { mutate: enterVideoSurvey } = useMutation(
    ["video.call.video.survey.enter"],
    (videoSurvey: EnterVideoSurvey) => VideoSurveyService.enter(videoSurvey),
    {
      onSuccess: (response) => {
        setVideoSurvey(response?.data);
      },
    }
  );

  const disableButtons = isAcquiringLocalTracks || isConnecting || loadingTask || hasError;

  const audioPermissionIsGranted = async () => {
    if (navigator.mediaDevices) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        if (stream) stream.getTracks()?.forEach((track) => track.stop());

        return true;
      } catch {
        NotificationToast.warning(
          `${
            currentUser?.name || "User"
          }, your microphone is in use by another application or has not been authorised to be used by the application.`
        );

        return false;
      }
    } else {
      return false;
    }
  };

  const videoPermissionIsGranted = async () => {
    if (navigator.mediaDevices) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });

        if (stream) stream.getTracks()?.forEach((track) => track.stop());

        return true;
      } catch {
        NotificationToast.warning(
          `${
            currentUser?.name || "User"
          }, your camera is in use by another application or has not been authorised to be used by the application.`
        );

        return false;
      }
    } else {
      return false;
    }
  };

  const getPermissions = async () => {
    if (navigator.mediaDevices) {
      const videoIsGranted = await videoPermissionIsGranted();
      const audioIsGranted = await audioPermissionIsGranted();

      if (!videoIsGranted || !audioIsGranted) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    } else {
      NotificationToast.warning(
        "Check your browser compatibility and update to the latest version if available."
      );
      setHasError(true);
    }
    setIsAcquiringPermissions(false);
  };

  useEffect(() => {
    if (roomState === "disconnected" && currentUser) {
      getPermissions();
    }
  }, [roomState, currentUser]);

  useEffect(() => {
    if (videoSurvey && !videoSurvey?.token && videoSurvey.message)
      NotificationToast.error(videoSurvey?.message);
  }, [videoSurvey]);

  const handleConnectInVideoCall = async () => {
    if (videoSurvey?.token) {
      videoConnect(videoSurvey?.token);
    }
  };

  const renderModals = () => {
    if (roomState === "disconnected" && !isFinished) {
      return (
        <div>
          <PreJoinModal
            loading={loadingTask}
            isConnecting={isConnecting}
            disableButtons={disableButtons}
            open={true}
            handleConnectInVideoCall={handleConnectInVideoCall}
            voiceToken={videoSurvey?.voiceToken}
            iceServers={videoSurvey?.iceServers}
          />
        </div>
      );
    }

    return null;
  };

  const renderPage = () => {
    if (isFinished) return <Leave room={room} />;

    if (isAcquiringPermissions && roomState === "disconnected") {
      return (
        <div style={{ zIndex: 9999 }} className="d-flex justify-content-center loader-overlay">
          <CircularProgress style={{ color: "#193564" }} />
        </div>
      );
    }

    if (roomState !== "disconnected" && !isFinished) {
      return (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className={classes.mainDiv}>
              <SurveyCall />
            </div>
          </ThemeProvider>
        </StyledEngineProvider>
      );
    }

    return null;
  };

  return (
    <Grid container>
      {renderModals()}
      {renderPage()}
    </Grid>
  );
};

export default SurveyScreen;
